<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  ArrowLeftIcon,
  AddIcon,
  DownloadIcon,
  LinkIcon,
  RefreshIcon,
  FilterIcon,
  EditIcon,
  DeleteIcon,
  AiIcon,
  SaveIcon,
  PlayIcon,
  FileIcon,
  InfoIcon,
  CloseIcon,
} from "../icons";
import {
  querySchema,
  formatValue,
  type Field,
  type QuerySpec,
} from "@yawhub/core";
import { api, errorText, dateTime, useSession } from "../api";
import ScaleValue from "../components/ScaleValue.vue";
import RowUpdated from "../components/RowUpdated.vue";
import TableAppearance from "../components/TableAppearance.vue";
import DatasetActivity from "../components/DatasetActivity.vue";
import RecordForm from "../components/RecordForm.vue";
import FieldEditor from "../components/FieldEditor.vue";
import QueryBuilder from "../components/QueryBuilder.vue";
import ResultView from "../components/ResultView.vue";
import SourcePanel from "../components/SourcePanel.vue";
import AutomationPanel from "../components/AutomationPanel.vue";
import DatasetSettings from "../components/DatasetSettings.vue";
import DatasetAvatar from "../components/DatasetAvatar.vue";
import ViewsPanel from "../components/ViewsPanel.vue";
import DashboardsPanel from "../components/DashboardsPanel.vue";
import PublicShare from "../components/PublicShare.vue";
import { filterDescription } from "../queryPresentation";
import ColumnSelection from "../components/ColumnSelection.vue";
import ColumnFilter from "../components/ColumnFilter.vue";
import ScheduleFields from "../components/ScheduleFields.vue";
import { activeDataset, datasetSection } from "../datasetNavigation";
const route = useRoute();
const router = useRouter(),
  settingsOpen = ref(false);
async function settingsSaved() {
  settingsOpen.value = false;
  await load();
}
function datasetDeleted() {
  void router.push({ path: "/", query: { deleted: "1" } });
}
const clone = (v: unknown) => JSON.parse(JSON.stringify(v));
const id = String(useRoute().params.id),
  base = "/api/datasets/" + id,
  session = useSession(),
  dataset = ref<any>(null),
  result = ref<any>(null),
  views = ref<any[]>([]),
  dashboards = ref<any[]>([]),
  saveDashboardId = ref(""),
  jobs = ref<any[]>([]),
  reports = ref<any[]>([]),
  artifacts = ref<any[]>([]),
  tab = computed({
    get: () => String(route.query.tab ?? "data"),
    set: (value: string) => {
      void router.push({ query: { ...route.query, tab: value } });
    },
  }),
  busy = ref(false),
  loading = ref(true),
  error = ref(""),
  message = ref(""),
  query = ref<QuerySpec>(querySchema.parse({})),
  showQuery = ref(false),
  selected = ref<string[]>([]),
  editing = ref<any>(undefined),
  deletePending = ref<any>(null),
  provenance = ref<any>(null),
  rejections = ref<any>(null);
const schemaFields = ref<Field[]>([]),
  schemaPreview = ref<any>(null),
  viewName = ref(""),
  viewKind = ref("table"),
  savingView = ref(false),
  reportName = ref(""),
  reportViews = ref<string[]>([]),
  reportEmail = ref(false),
  reportCron = ref("0 9 * * 1"),
  reportTimezone = ref("Europe/Copenhagen"),
  addingReport = ref(false),
  exportOpen = ref(false),
  shareOpen = ref(false),
  shareToggle = ref<HTMLButtonElement>(),
  activeDashboardId = ref<string>();
const dataColumns = ref<string[]>();
const scaleBars = ref<string[]>([]),
  showUpdatedAt = ref(true);
const selectedColumns = computed({
  get: () =>
    dataColumns.value ??
    fields.value.filter((f) => f.active && !f.hidden).map((f) => f.id),
  set: (value: string[]) => {
    dataColumns.value = value;
  },
});
const fields = computed<Field[]>(() => dataset.value?.schema?.fields ?? []),
  visibleFields = computed(() =>
    fields.value.filter(
      (f) => f.active && selectedColumns.value.includes(f.id),
    ),
  ),
  can = (cap: string) =>
    dataset.value?.capabilities?.includes(cap) &&
    (dataset.value.write_state === "active" ||
      ["read", "export"].includes(cap));
const removedFields = computed(() =>
  fields.value.filter(
    (f) =>
      f.active &&
      schemaFields.value.some((next) => next.id === f.id && !next.active),
  ),
);
const canShareDashboard = computed(
  () =>
    tab.value === "dashboard" &&
    !!activeDashboardId.value &&
    can("export") &&
    ["owner", "admin"].includes(
      session.organizations.find((o: any) => o.id === session.organization)
        ?.role,
    ),
);
watch([tab, activeDashboardId], () => {
  exportOpen.value = false;
  shareOpen.value = false;
});
async function closeShare() {
  shareOpen.value = false;
  await nextTick();
  shareToggle.value?.focus();
}
watch(
  () => dataset.value?.name,
  (name) => {
    if (name) document.title = name + " · Yawhub";
  },
);
onUnmounted(() => {
  document.title = "Yawhub";
});
watch(
  schemaFields,
  () => {
    schemaPreview.value = null;
  },
  { deep: true },
);
const allTabs = computed(() => [
  { id: "data", label: "Rækker" },
  { id: "dashboard", label: "Dashboards" },
  ...(can("sources") ? [{ id: "sources", label: "Kilder" }] : []),
  ...(can("schema") ? [{ id: "schema", label: "Felter" }] : []),
  ...(can("automate") ? [{ id: "rules", label: "Regler" }] : []),
  { id: "reports", label: "Rapporter" },
  { id: "jobs", label: "Kørsler" },
  ...(dataset.value?.canViewActivity
    ? [{ id: "activity", label: "Aktivitet" }]
    : []),
]);
const tabs = computed(() =>
  allTabs.value.filter(
    (t) => datasetSection(t.id) === datasetSection(tab.value),
  ),
);
watch([tab, dataset], () => {
  if (tab.value === "views") {
    tab.value = "dashboard";
    return;
  }
  if (dataset.value && !allTabs.value.some((t) => t.id === tab.value))
    tab.value = "data";
});
function applyColumnFilter(q: QuerySpec) {
  query.value = q;
  void runQuery();
}
async function load() {
  try {
    dataset.value = await api(base);
    activeDataset.value = dataset.value;
    schemaFields.value = clone(dataset.value.schema.fields);
    await Promise.all([queryData(), loadSupporting()]);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}
async function loadSupporting() {
  const all = await Promise.allSettled([
    api(base + "/views"),
    api(base + "/jobs"),
    api(base + "/reports"),
    can("export") ? api(base + "/artifacts") : Promise.resolve([]),
    api(base + "/dashboards"),
  ]);
  const refs = [views, jobs, reports, artifacts, dashboards];
  all.forEach((r, i) => {
    if (r.status === "fulfilled") refs[i]!.value = r.value;
  });
}
async function action(fn: () => Promise<void>) {
  busy.value = true;
  error.value = "";
  message.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function queryData(next = false) {
  const q = {
    ...query.value,
    cursor: next ? result.value?.nextCursor : undefined,
  };
  result.value = await api(base + "/query", { method: "POST", body: q });
  query.value = q;
  selected.value = [];
}
async function runQuery() {
  await action(async () => {
    await queryData();
  });
}
async function saveView() {
  await action(async () => {
    const view = await api(base + "/views", {
      method: "POST",
      body: {
        name: viewName.value,
        kind: viewKind.value,
        dashboardId: saveDashboardId.value || undefined,
        query: query.value,
        config: {
          refreshSeconds: 60,
          fieldIds: selectedColumns.value,
          scaleBars: scaleBars.value,
          showUpdatedAt: showUpdatedAt.value,
        },
      },
    });
    views.value.push(view);
    savingView.value = false;
    message.value = "Kortet er gemt på dashboardet.";
  });
}
async function remove() {
  await action(async () => {
    await api(base + "/records/" + deletePending.value.id, {
      method: "DELETE",
      revision: deletePending.value.revision,
    });
    deletePending.value = null;
    await queryData();
    message.value = "Rækken er slettet.";
  });
}
async function previewSchema() {
  await action(async () => {
    schemaPreview.value = await api(base + "/schema/preview", {
      method: "POST",
      body: { fields: schemaFields.value },
    });
    if (!schemaPreview.value.invalid && !removedFields.value.length)
      await applySchema();
  });
}
async function applySchema() {
  await api(base + "/schema/" + schemaPreview.value.id + "/activate", {
    method: "POST",
  });
  schemaPreview.value = null;
  await load();
  message.value = "Felterne er gemt.";
}
async function activateSchema() {
  await action(applySchema);
}
async function exportData(format: "csv" | "xlsx") {
  await action(async () => {
    await api(base + "/exports", {
      method: "POST",
      body: {
        format,
        query: query.value,
        ...(selected.value.length ? { recordIds: selected.value } : {}),
      },
    });
    exportOpen.value = false;
    message.value =
      "Eksporten er sat i kø. Filen vises under Rapporter, når den er klar.";
    await loadSupporting();
  });
}
async function createReport() {
  await action(async () => {
    const r = await api(base + "/reports", {
      method: "POST",
      body: {
        name: reportName.value,
        viewIds: reportViews.value,
        email: reportEmail.value,
      },
    });
    await api(base + "/schedules", {
      method: "POST",
      body: {
        kind: "report",
        targetId: r.id,
        cron: reportCron.value,
        timezone: reportTimezone.value,
      },
    });
    addingReport.value = false;
    await loadSupporting();
    message.value = "Rapporten er gemt og planlagt.";
  });
}
async function download(artifact: any) {
  await action(async () => {
    const r = await fetch(base + "/artifacts/" + artifact.id + "/download", {
      headers: { "x-organization-id": session.organization },
    });
    if (!r.ok) throw new Error((await r.json()).message);
    const url = URL.createObjectURL(await r.blob()),
      a = document.createElement("a");
    a.href = url;
    a.download = artifact.filename;
    a.click();
    URL.revokeObjectURL(url);
  });
}
let poll: ReturnType<typeof setInterval>;
onMounted(async () => {
  await load();
  poll = setInterval(() => {
    void loadSupporting();
    if (dataset.value?.favicon_status === "pending")
      void api(base)
        .then((d) => {
          dataset.value = d;
        })
        .catch(() => {});
  }, 5000);
});
onUnmounted(() => {
  clearInterval(poll);
  if (activeDataset.value?.id === id) activeDataset.value = null;
});
const status = (s: string) =>
  ({
    pending: "I kø",
    running: "Kører",
    retry: "Prøver igen",
    succeeded: "Afsluttet",
    failed: "Fejlet",
    cancelled: "Annulleret",
  })[s] ?? s;
</script>
<template>
  <div v-if="loading" class="empty-state" role="status">Åbner datasættet…</div>
  <template v-else-if="dataset"
    ><RouterLink to="/" class="back-link"
      ><ArrowLeftIcon :size="16" />Alle datasæt</RouterLink
    >
    <div class="page-heading dataset-heading">
      <div>
        <h1 class="dataset-name">
          <DatasetAvatar :dataset="dataset" :size="30" />{{ dataset.name }}
        </h1>
        <p class="subtitle">
          {{ dataset.description || "Et samlet overblik over dine data." }}
        </p>
        <div class="dataset-meta">
          <span class="status-dot" />Schema v{{ dataset.active_schema_version
          }}<span>·</span
          ><span
            >Revision
            {{ result?.metadata.dataRevision ?? dataset.data_revision }}</span
          ><span v-if="result?.metadata.lastChangedAt"
            >· Opdateret {{ dateTime(result.metadata.lastChangedAt) }}</span
          >
        </div>
      </div>
      <div class="heading-actions">
        <button
          v-if="
            dataset.capabilities?.includes('schema') ||
            dataset.canManageLifecycle
          "
          class="button secondary"
          @click="settingsOpen = true"
        >
          <EditIcon :size="17" />Indstillinger
        </button>
        <div v-if="can('export')" class="dropdown-anchor">
          <button
            ref="shareToggle"
            class="button secondary"
            :aria-expanded="exportOpen"
            aria-controls="dataset-share-menu"
            @click="exportOpen = !exportOpen"
          >
            <LinkIcon :size="17" />Del{{
              selected.length ? " (" + selected.length + ")" : ""
            }}
          </button>
          <div v-if="exportOpen" id="dataset-share-menu" class="action-menu">
            <button
              v-if="canShareDashboard"
              @click="
                exportOpen = false;
                shareOpen = true;
              "
            >
              Offentligt delingslink
            </button>
            <button @click="exportData('csv')">CSV-fil</button
            ><button @click="exportData('xlsx')">Excel med metadata</button>
          </div>
        </div>
        <button
          v-if="can('write')"
          class="button primary"
          @click="editing = null"
        >
          <AddIcon :size="17" />Tilføj række
        </button>
      </div>
    </div>
    <p v-if="dataset.write_state === 'archived'" class="notice" role="status">
      Datasættet er arkiveret. Du kan læse og eksportere data. Registrering og
      planlagte kørsler er stoppet.
    </p>
    <DatasetSettings
      v-if="settingsOpen"
      :dataset="dataset"
      @close="settingsOpen = false"
      @saved="settingsSaved"
      @deleted="datasetDeleted"
    />
    <PublicShare
      v-if="shareOpen && canShareDashboard"
      :key="activeDashboardId"
      :dataset="id"
      :dashboard="activeDashboardId"
      modal
      @close="closeShare"
    />
    <nav class="tabs" aria-label="Datasættets sider">
      <button
        v-for="t in tabs"
        :key="t.id"
        :class="{ active: tab === t.id }"
        @click="tab = t.id"
      >
        {{ t.label
        }}<span
          v-if="
            t.id === 'jobs' &&
            jobs.some((j) => ['pending', 'running', 'retry'].includes(j.status))
          "
          class="tab-dot"
        />
      </button>
    </nav>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p v-if="message" class="notice success-notice" role="status">
      {{ message
      }}<button
        class="icon-button"
        aria-label="Luk besked"
        @click="message = ''"
      >
        <CloseIcon :size="16" />
      </button>
    </p>
    <DatasetActivity v-if="tab === 'activity'" :dataset="id" />
    <section v-if="tab === 'data'" class="data-section">
      <div class="data-toolbar">
        <div>
          <strong>{{ result?.rows.length ?? 0 }} rækker på siden</strong
          ><span v-if="selected.length" class="selection-count"
            >{{ selected.length }} valgt</span
          >
        </div>
        <div>
          <button class="button secondary" @click="showQuery = !showQuery">
            <FilterIcon :size="16" />{{
              showQuery ? "Luk forespørgsel" : "Filtre & beregning"
            }}</button
          ><button
            class="icon-button"
            :disabled="busy"
            aria-label="Opdatér data"
            @click="runQuery"
          >
            <RefreshIcon :size="18" />
          </button>
        </div>
      </div>
      <QueryBuilder
        v-if="showQuery"
        v-model="query"
        :fields="fields"
        :busy="busy"
        @run="runQuery"
      />
      <div v-if="query.metrics.length">
        <ResultView :result="result" kind="table" /><button
          v-if="can('write')"
          class="button secondary save-view-button"
          @click="savingView = true"
        >
          Gem på dashboard
        </button>
      </div>
      <ColumnSelection
        v-if="!query.metrics.length"
        v-model="selectedColumns"
        :fields="fields"
      />
      <TableAppearance
        v-if="!query.metrics.length"
        v-model:bars="scaleBars"
        v-model:updated="showUpdatedAt"
        :fields="fields"
        :columns="selectedColumns"
      />
      <p v-if="!query.metrics.length && !visibleFields.length" class="notice">
        Vælg de kolonner, du vil se, under Kolonner.
      </p>
      <div v-if="query.filters.length" class="active-filter-list">
        <button
          v-for="(filter, i) in query.filters"
          :key="i"
          class="button secondary"
          @click="
            applyColumnFilter({
              ...query,
              filters: query.filters.filter((_, index) => index !== i),
            })
          "
        >
          {{ filterDescription(filter, fields) }}
          <CloseIcon :size="13" /><span class="sr-only"
            >Fjern filter</span
          ></button
        ><button
          class="text-button"
          @click="applyColumnFilter({ ...query, filters: [] })"
        >
          Nulstil filtre
        </button>
      </div>
      <div
        v-if="
          !query.metrics.length && !result?.rows.length && query.filters.length
        "
        class="empty-state"
      >
        <h3>Ingen rækker matcher</h3>
        <p>Prøv en anden værdi, eller fjern et filter.</p>
      </div>
      <div
        v-else-if="!query.metrics.length && !result?.rows.length"
        class="empty-state"
      >
        <h3>Der er plads til din første række</h3>
        <p>Tilføj data manuelt, eller forbind en kilde under Opsætning.</p>
        <button
          v-if="can('write')"
          class="button primary"
          @click="editing = null"
        >
          <AddIcon :size="17" />Tilføj række
        </button>
      </div>
      <div
        v-else-if="!query.metrics.length && visibleFields.length"
        class="table-scroll main-table"
        role="region"
        aria-label="Datasættets rækker"
        tabindex="0"
      >
        <table>
          <thead>
            <tr>
              <th class="check-cell">
                <input
                  type="checkbox"
                  aria-label="Vælg alle rækker på siden"
                  :checked="
                    result.rows.length > 0 &&
                    result.rows.every((r: any) => selected.includes(r.id))
                  "
                  @change="
                    selected = ($event.target as HTMLInputElement).checked
                      ? result.rows.map((r: any) => r.id)
                      : []
                  "
                />
              </th>
              <th
                v-for="f in visibleFields"
                :key="f.id"
                :class="{
                  numeric: ['integer', 'decimal', 'rating'].includes(f.type),
                }"
              >
                <ColumnFilter
                  :field="f"
                  :query="query"
                  :disabled="busy"
                  @apply="applyColumnFilter"
                />
              </th>
              <th v-if="showUpdatedAt">Senest opdateret</th>
              <th class="row-actions-heading">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in result.rows"
              :key="r.id"
              :class="{ selected: selected.includes(r.id) }"
            >
              <td class="check-cell">
                <input
                  v-model="selected"
                  type="checkbox"
                  :value="r.id"
                  :aria-label="'Vælg række ' + r.id"
                />
              </td>
              <td
                v-for="f in visibleFields"
                :key="f.id"
                :class="{
                  numeric: ['integer', 'decimal', 'rating'].includes(f.type),
                  empty: r.data[f.id] === null,
                }"
                @dblclick="can('write') && !r.last_run_id && (editing = r)"
              >
                <ScaleValue
                  :dataset="id"
                  :field="f"
                  :value="r.data[f.id]"
                  :bar="scaleBars.includes(f.id)"
                />
              </td>
              <td v-if="showUpdatedAt"><RowUpdated :value="r.updated_at" /></td>
              <td class="row-actions">
                <button
                  class="icon-button"
                  aria-label="Se datakilde"
                  @click="
                    action(async () => {
                      provenance = await api(
                        base + '/records/' + r.id + '/provenance',
                      );
                    })
                  "
                >
                  <InfoIcon :size="16" /></button
                ><button
                  v-if="can('write') && !r.last_run_id"
                  class="icon-button"
                  aria-label="Redigér række"
                  @click="editing = r"
                >
                  <EditIcon :size="16" /></button
                ><button
                  v-if="can('write')"
                  class="icon-button danger"
                  aria-label="Slet række"
                  @click="deletePending = r"
                >
                  <DeleteIcon :size="16" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <button
          v-if="can('write') && !query.metrics.length"
          class="text-button"
          @click="
            viewKind = 'table';
            savingView = true;
          "
        >
          Gem tabel på dashboard
        </button>
        <span
          >{{
            query.filters.length
              ? query.filters.length + " aktive filtre"
              : "Alle aktive rækker"
          }}
          · Kildestyrede rækker redigeres gennem mappingen.</span
        ><button
          v-if="result?.nextCursor"
          class="button secondary"
          :disabled="busy"
          @click="action(() => queryData(true))"
        >
          Næste side</button
        ><button v-else-if="query.cursor" class="text-button" @click="runQuery">
          Første side
        </button>
      </div>
      <div v-if="deletePending" class="inline-confirm">
        <div>
          <strong>Slet denne række?</strong>
          <p>Identiteten bevares, så en import ikke opretter den igen.</p>
        </div>
        <button class="button secondary" @click="deletePending = null">
          Annullér</button
        ><button class="button destructive" :disabled="busy" @click="remove">
          Slet række
        </button>
      </div>
      <div v-if="provenance" class="inline-editor">
        <div class="section-heading">
          <h3>Rækkens oprindelse</h3>
          <button
            class="icon-button"
            aria-label="Luk oprindelse"
            @click="provenance = null"
          >
            <CloseIcon :size="18" />
          </button>
        </div>
        <dl class="metadata-grid">
          <dt>Kilde</dt>
          <dd>{{ provenance.source_name }} · {{ provenance.kind }}</dd>
          <dt>Identitet</dt>
          <dd>{{ provenance.identity_key }}</dd>
          <dt>Revision</dt>
          <dd>{{ provenance.revision }}</dd>
          <dt>Kildens tidspunkt</dt>
          <dd>{{ dateTime(provenance.source_updated_at) }}</dd>
          <dt>Sidst ændret</dt>
          <dd>{{ dateTime(provenance.updated_at) }}</dd>
          <dt>Importkørsel</dt>
          <dd>{{ provenance.last_run_id ?? "Manuel indtastning" }}</dd>
        </dl>
      </div>
    </section>
    <DashboardsPanel
      v-if="tab === 'views' || tab === 'dashboard'"
      :dataset="id"
      :fields="fields"
      :views="views"
      :can-write="!!can('write')"
      @selected="activeDashboardId = $event"
      @changed="loadSupporting"
    />
    <form
      v-if="savingView && tab === 'data'"
      class="inline-editor form-stack"
      @submit.prevent="saveView"
    >
      <h3>Gem kort</h3>
      <label v-if="dashboards.length && viewKind !== 'form'"
        >Dashboard<select v-model="saveDashboardId">
          <option value="">{{ dashboards[0]?.name }}</option>
          <option v-for="d in dashboards" :key="d.id" :value="d.id">
            {{ d.name }}
          </option>
        </select></label
      >
      <div class="form-grid">
        <label
          >Navn<input
            v-model="viewName"
            placeholder="Fx Ugentlig omsætning"
            required /></label
        ><label
          >Korttype<select v-model="viewKind">
            <option value="table">Tabel</option>
            <option
              value="line"
              :disabled="!query.metrics.length || !query.dimensions.length"
            >
              Linjegraf
            </option>
            <option
              value="bar"
              :disabled="!query.metrics.length || !query.dimensions.length"
            >
              Søjlegraf
            </option>
            <option value="kpi" :disabled="!query.metrics.length">KPI</option>
            <option value="form" :disabled="!!query.metrics.length">
              Formular
            </option>
          </select></label
        >
      </div>
      <div class="form-actions">
        <button
          type="button"
          class="button secondary"
          @click="savingView = false"
        >
          Annullér</button
        ><button class="button primary" :disabled="busy">Gem kort</button>
      </div>
    </form>
    <SourcePanel
      v-if="tab === 'sources'"
      :dataset="id"
      :fields="fields"
      @changed="loadSupporting"
      @schema-changed="load"
    />
    <section v-if="tab === 'schema'">
      <div class="section-heading">
        <div>
          <h2>Felterne i dit datasæt</h2>
          <p class="muted">
            Omdøb eller tilføj felter, og vælg hvilke værdier de må indeholde.
            Ændringer kontrolleres, før de tages i brug.
          </p>
        </div>
        <span class="status-pill neutral"
          >Version {{ dataset.active_schema_version }}</span
        >
      </div>
      <fieldset class="schema-edit-fields" :disabled="busy">
        <FieldEditor
          v-model="schemaFields"
          existing
          @update:model-value="schemaPreview = null"
        />
      </fieldset>
      <p class="muted small">
        Et skjult felt er stadig tilgængeligt for brugere med læseadgang.
        Deaktivering fjerner feltets værdier fra rækkerne. Skjul i indtastning
        påvirker kun formularen.
      </p>
      <div class="form-actions">
        <button
          class="button secondary"
          :disabled="busy"
          @click="
            schemaFields = clone(fields);
            schemaPreview = null;
          "
        >
          Fortryd ændringer</button
        ><button class="button primary" :disabled="busy" @click="previewSchema">
          {{ busy ? "Gemmer…" : "Gem felter" }}
        </button>
      </div>
      <div v-if="schemaPreview" class="inline-editor">
        <h3>
          {{
            schemaPreview.invalid
              ? "Felterne kan ikke gemmes endnu"
              : "Fjern felter og deres værdier?"
          }}
        </h3>
        <p v-if="removedFields.length">
          Du deaktiverer {{ removedFields.map((f) => f.label).join(", ") }}.
          Værdierne fjernes fra eksisterende rækker og kan ikke gendannes ved at
          genaktivere feltet.
        </p>
        <p>
          {{ schemaPreview.total }} rækker undersøgt ·
          {{ schemaPreview.valid }} gyldige ·
          {{ schemaPreview.invalid }} ugyldige
        </p>
        <ul v-if="schemaPreview.errors.length">
          <li v-for="e in schemaPreview.errors" :key="e.recordId">
            {{ e.issues.map((x: any) => x.message).join(" ") }}
          </li>
        </ul>
        <button
          v-if="!schemaPreview.invalid"
          class="button primary"
          :disabled="busy || schemaPreview.invalid > 0"
          @click="activateSchema"
        >
          Bekræft fjernelse og gem
        </button>
      </div>
      <ViewsPanel
        :dataset="id"
        :fields="fields"
        :views="views.filter((v) => v.kind === 'form')"
        :can-write="can('write')"
        mode="forms"
        @changed="loadSupporting"
      />
    </section>
    <AutomationPanel
      v-if="tab === 'rules'"
      :dataset="id"
      :views="views"
      :fields="fields"
    />
    <section v-if="tab === 'reports'">
      <div class="section-heading">
        <div>
          <h2>Rapporter & eksport</h2>
          <p class="muted">
            Planlagte overblik og filer, du kan tage med videre.
          </p>
        </div>
        <button
          v-if="can('automate') && can('export')"
          class="button primary"
          @click="addingReport = !addingReport"
        >
          <AddIcon :size="17" />Ny rapport
        </button>
      </div>
      <form
        v-if="addingReport"
        class="inline-editor form-stack"
        @submit.prevent="createReport"
      >
        <label>Rapportnavn<input v-model="reportName" required /></label>
        <fieldset>
          <legend>Vælg op til fem dashboardkort</legend>
          <label
            v-for="v in views.filter((v) => v.kind !== 'form')"
            :key="v.id"
            class="checkbox"
            ><input
              v-model="reportViews"
              :value="v.id"
              type="checkbox"
              :disabled="reportViews.length >= 5 && !reportViews.includes(v.id)"
            />{{ v.name }}</label
          >
          <p v-if="!views.length">Gem et kort på et dashboard først.</p>
        </fieldset>
        <ScheduleFields
          v-model="reportCron"
          v-model:timezone="reportTimezone"
          :disabled="busy"
        />
        <label class="checkbox"
          ><input v-model="reportEmail" type="checkbox" />Send et email-link til
          min verificerede adresse</label
        >
        <div class="form-actions">
          <button
            type="button"
            class="button secondary"
            @click="addingReport = false"
          >
            Annullér</button
          ><button
            class="button primary"
            :disabled="busy || !reportViews.length"
          >
            Gem og planlæg rapport
          </button>
        </div>
      </form>
      <article v-for="r in reports" :key="r.id" class="rule-row">
        <FileIcon :size="23" />
        <div>
          <h3>{{ r.name }}</h3>
          <p class="muted">{{ r.view_ids.length }} kort · HTML</p>
        </div>
        <button
          class="button secondary"
          :disabled="busy"
          @click="
            action(async () => {
              await api(base + '/reports/' + r.id + '/run', { method: 'POST' });
              message = 'Rapporten er sat i kø.';
              await loadSupporting();
            })
          "
        >
          <PlayIcon :size="16" />Kør nu
        </button>
      </article>
      <h3 class="section-space">Filer klar til download</h3>
      <p class="muted small">
        Filer udløber efter 24 timer. Din aktuelle adgang kontrolleres ved
        download.
      </p>
      <div v-if="!artifacts.length" class="empty-state compact">
        <DownloadIcon :size="25" />
        <p>Eksportér et datasæt eller kør en rapport. Filen vises her.</p>
      </div>
      <div v-for="a in artifacts" :key="a.id" class="artifact-row">
        <FileIcon :size="23" />
        <div>
          <strong>{{ a.filename }}</strong
          ><span>Tilgængelig til {{ dateTime(a.expires_at) }}</span>
        </div>
        <button class="button secondary" :disabled="busy" @click="download(a)">
          <DownloadIcon :size="16" />Hent fil
        </button>
      </div>
    </section>
    <section v-if="tab === 'jobs'">
      <div class="section-heading">
        <div>
          <h2>Kørsler</h2>
          <p class="muted">Import, eksport og handlinger. Opdateres løbende.</p>
        </div>
        <button class="button secondary" @click="loadSupporting">
          <RefreshIcon :size="16" />Opdatér
        </button>
      </div>
      <div v-if="!jobs.length" class="empty-state">
        Ingen kørsler endnu. Start en import under Kilder.
      </div>
      <div v-else class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Startet</th>
              <th>Resultat</th>
              <th>Handling</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="j in jobs" :key="j.id">
              <td>
                {{
                  (
                    {
                      import: "Import",
                      export: "Eksport",
                      automation: "Regel",
                      report: "Rapport",
                      email: "Email",
                    } as any
                  )[j.kind] ?? j.kind
                }}
              </td>
              <td>
                <span
                  class="status-pill"
                  :class="
                    j.status === 'succeeded'
                      ? 'success'
                      : j.status === 'failed'
                        ? 'danger'
                        : j.status === 'running'
                          ? 'warning'
                          : 'neutral'
                  "
                  >{{ status(j.status) }}</span
                >
              </td>
              <td>
                {{ dateTime(j.started_at ?? j.created_at)
                }}<small v-if="j.finished_at && j.started_at" class="block"
                  >{{
                    (
                      (new Date(j.finished_at).valueOf() -
                        new Date(j.started_at).valueOf()) /
                      1000
                    ).toFixed(1)
                  }}
                  sek.</small
                >
              </td>
              <td>
                <span v-if="j.counters" class="run-counters"
                  >{{ j.counters.created }} nye ·
                  {{ j.counters.updated }} ændrede ·
                  {{ j.counters.unchanged }} uændrede ·
                  {{ j.counters.rejected }} afviste</span
                >
                <p v-if="j.error" class="field-error">{{ j.error }}</p>
                <small v-if="j.drift?.length" class="field-error"
                  >Nye kildestier: {{ j.drift.join(", ") }}. Gennemgå
                  mappingen.</small
                >
              </td>
              <td>
                <button
                  v-if="j.counters?.rejected && can('sources')"
                  class="text-button"
                  @click="
                    action(async () => {
                      rejections = await api(
                        base + '/jobs/' + j.id + '/rejections',
                      );
                    })
                  "
                >
                  Se afviste</button
                ><button
                  v-if="
                    ['pending', 'running', 'retry'].includes(j.status) &&
                    can('sources')
                  "
                  class="text-button"
                  @click="
                    action(async () => {
                      await api(base + '/jobs/' + j.id + '/cancel', {
                        method: 'POST',
                      });
                      await loadSupporting();
                    })
                  "
                >
                  Annullér
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="rejections" class="inline-editor">
        <div class="section-heading">
          <h3>Afviste rækker</h3>
          <button
            class="icon-button"
            aria-label="Luk"
            @click="rejections = null"
          >
            <CloseIcon :size="17" />
          </button>
        </div>
        <p v-for="(r, i) in rejections" :key="i">
          Række {{ r.row_locator }}: {{ r.reason }}
        </p>
      </div>
    </section>
    <RecordForm
      v-if="editing !== undefined"
      :dataset="id"
      :fields="fields"
      :record="editing"
      @close="editing = undefined"
      @saved="load"
    />
  </template>
  <div v-else class="error" role="alert">
    {{ error || "Datasættet blev ikke fundet." }}
    <RouterLink to="/">Tilbage til dine datasæt</RouterLink>
  </div>
</template>
<style scoped lang="scss" src="../styles/pages/Dataset.scss"></style>
